/** @module csi/util */
define([
	"csi",
	"csi/_base/underscore",
	"csi/_base/xpath",
	"csi/json/JSNode"
], function(csi, _, xpath, JSNode) {

	const BOOLEAN_ONE = "1";
	const BOOLEAN_TRUE = "true";

	/**
	 * return true if given DOMNode is a text element
	 * @param {DOMNode} e
	 */
	function isTextNode(e) {
		return e.nodeType === 3;
	}

	/**
	 * Create new XML document, either blank or containing given XML content
	 * @param {string} [content]
	 * @return {XMLDocument}
	 */
	function newXMLDocument(content) {
		content = (content || "").trim();

		if (!content) {
			return document.implementation.createDocument("", "");
		}

		const doc = new DOMParser().parseFromString(content, "text/xml");
		const parserError = doc.querySelector("parsererror");
		if (parserError) {
			throw new Error("Error parsing XML content:\n" + parserError.innerText);
		}

		return doc;
	}

	/**
	 * Serialize XML node to string containing XML content
	 * @param {Document | Element} node
	 * @param {boolean} [preserveWhitespace]
	 * @return {string}
	 */
	function serializeXML(node, preserveWhitespace) {
		if (preserveWhitespace) {
			const element = node.documentElement || node;
			element.setAttribute("xml:space", "preserve");
		}

		return node ? new XMLSerializer().serializeToString(node).trim() : "";
	}

	/**
	 * Given request parameters and a parameter holding an array, split into N arrays of almost equal lengths
	 * @param {Object} requestParams
	 * @param {string} param
	 * @param {number} N - number of suggested requests
	 * @return {any[][]}
	 */
	function splitRequestArray(requestParams, param, N) {
		var a = requestParams[param];
		var l = a.length;
		var m = Math.ceil(l / N);
		var n = Math.ceil(l / m);
		var ret = [];
		var obj;

		for (var i = 0; i < n; ++i) {
			obj = {};
			obj[param] = a.slice(i * m, (i + 1) * m);
			ret.push(obj);
		}

		return ret;
	}

	/**
	* Given request parameters and names of begin/end parameters, split page boundaries into N begin/end pairs whose counts are almost equal
	* @param {Object} requestParams
	* @param {string} paramBegin
	* @param {string} paramEnd
	* @param {number} N - number of suggested requests
	* @return {number[][]}
	*/
	function splitRequestPages(requestParams, paramBegin, paramEnd, N) {
		var begin = requestParams[paramBegin];
		var end = requestParams[paramEnd];
		var l = end - begin + 1;
		var m = Math.ceil(l / N);        // nodes per request, i.e Math.ceil(20 / 6) = 4
		var n = Math.ceil(l / m);        // number of requests to satisfy full request, i.e. Math.ceil(20 / 4) = 5 (not 6)
		var ret = [];
		var obj;

		for (var i = 0; i < n; ++i) {
			obj = {};
			obj[paramBegin] = begin + (i * m);
			obj[paramEnd] = Math.min(end, begin + ((i + 1) * m) - 1);
			ret.push(obj);
		}

		return ret;
	}

	const TRAVERSE_DELETE = 1;
	const TRAVERSE_PRUNE = 2;
	const TRAVERSE_RESULT = 3;
	const TRAVERSE_RETURN = 4;

	function traverseNode(/*DOMNode*/element, /*Function*/callback, /*Boolean*/bDepthFirst, /*DOMNode[]?*/aResults) {
		// summary: traverse given DOM element, calling callback function for each element
		//
		//   Possible return values of callback function:
		// 		TRAVERSE_DELETE: prune traversal and delete current element
		// 		TRAVERSE_PRUNE: prune traversal of current element
		// 		TRAVERSE_RESULT: collect in result array (assumed given)
		// 		TRAVERSE_RETURN: prune traversal and return current element
		// 		nothing/undefined: keep traversing

		if (!element) {
			return;
		}

		var a = [element];
		var e;
		var ret;

		while (a.length) {
			e = a.shift();
			ret = callback(e);

			if (ret === TRAVERSE_DELETE) {
				if (e.parentNode) {
					e.parentNode.removeChild(e);
				}

				// if root element is removed, then return immediately
				if (e == element) {
					return;
				}

				continue;
			} else if (ret === TRAVERSE_PRUNE) {
				continue;
			} else if (ret === TRAVERSE_RESULT) {
				aResults.push(e);
			} else if (ret === TRAVERSE_RETURN) {
				return e;
			}

			if (e.childNodes) {
				if (bDepthFirst) {
					e = e.lastChild;
					while (e) {
						a.unshift(e);
						e = e.previousSibling;
					}
				} else {
					e = e.firstChild;
					while (e) {
						a.push(e);
						e = e.nextSibling;
					}
				}
			}
		}
	}

	return {
		// traverseNode function return constants
		TRAVERSE_DELETE,
		TRAVERSE_PRUNE,
		TRAVERSE_RESULT,
		TRAVERSE_RETURN,

		isTextNode,
		newXMLDocument,
		serializeXML,
		splitRequestArray,
		splitRequestPages,
		traverseNode,

		truncate: function(x, pre) {
			pre = pre || 1;
			return Math.floor((1. / pre) * x + 0.5) * pre;
		},

		uuid: function() {
			// borrowed from https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
			return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, function(c) {
				return (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16);
			});
		},

		xmlToString: function(xml) {
			var formatted = '';
			var reg = /(>)(<)(\/*)/g;
			xml = xml.replace(reg, '$1\n$2$3');
			var pad = 0;
			xml.split('\n').forEach(function(node) {
				var indent = 0;
				node = node.trim();
				if (node.match(/.+<\/\w[^>]*>$/)) {
					indent = 0;
				} else if (node.match(/^<\/\w/)) {
					if (pad != 0) {
						pad -= 1;
					}
				} else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
					indent = 1;
				} else {
					indent = 0;
				}

				var padding = '';
				for (var i = 0; i < pad; i++) {
					padding += '    ';
				}
				formatted += padding + _.escape(node) + '\n';
				pad += indent;
			});

			return formatted;
		},

		getElements: function(xml, filter) {
			// makes deep copies of elements satisfying the given filter
			return xml ? csi.transform(this.sn(xml, filter), function(obj) {
				return obj.cloneNode(true);
			}) : null;
		},

		ssn: function(contextNode, s) {
			// summary: select nodes using XPath
			if (!contextNode)
				return null;

			if (contextNode instanceof JSNode)
				return contextNode.selectSingleNode(s);

			var nodes = xpath.selectNodes(contextNode, s);
			return (nodes && nodes.length) ? nodes[0] : null;
		},

		sn: function(contextNode, s) {
			// summary: select nodes using XPath
			if (!contextNode)
				return null;

			if (contextNode instanceof JSNode)
				return contextNode.selectNodes(s);

			var nodes = xpath.selectNodes(contextNode, s);
			return (nodes && nodes.length) ? nodes : null;
		},

		// XML extraction functions
		getDef: function(xml, filter, f, bCall) {
			// <XML ...>
			//         <Tag ... />
			if (!xml) return null;

			var node;

			if (filter == null) {
				// the special case when the node is filtered already
				node = xml;
			} else {
				try {
					node = this.ssn(xml, filter);
				} catch (ex) {
					return null;
				}
			}

			if (node) {
				var o = bCall ? f(node) : new f(node);
				if (o && (!o.isValid || o.isValid()))
					return o;
			}

			return null;
		},

		getDefs: function(xml, filter, f, bCall) {
			// <XML ...>
			//         <Tag ... />
			//         <Tag ... />
			if (!xml) return null;

			var nodes;

			if (filter == null && xml.length != null) {
				// the special case when nodes are filtered already
				nodes = xml;
			} else {
				try {
					nodes = this.sn(xml, filter);
					if (!nodes || (nodes.length == 0))
						return null;
				} catch (ex) {
					console.warn(ex);
					return null;
				}
			}

			var a = [];
			var bValid = false;
			var o;

			for (var i = 0; i < nodes.length; ++i) {
				o = bCall ? f(nodes[i]) : new f(nodes[i]);

				if (o && (!o.isValid || o.isValid())) {
					a.push(o);
					bValid = true;
				}
			}

			return bValid ? a : null;
		},

		getFileExtension: function(/*String*/fileName) {
			// summary: Return the file extension from file name
			return fileName.split('.').pop().toLowerCase();
		},

		getProperties: function(xml, filter, p) {
			// <XML ...>
			//         <Tag P="..." />
			//         <Tag P="..." />
			return xml ? csi.transform(this.sn(xml, filter), function(obj) {return obj.getAttribute(p);}) : null;
		},

		getProperty: function(xml, p, defaultValue) {
			// <XML P="...">
			try {
				var v = xml ? xml.getAttribute(p) : null;

				// the value could be a number when coming from Site_Merged.js so cast to String just in case
				if (v != null) return v.toString();
			} catch (ex) {
			}

			return defaultValue;
		},

		/**
		 * Read boolean property from given element; if not specified, then return null rather than false
		 * @param {Element} xml
		 * @param {string} p
		 * @param {boolean} [defaultValue]
		 * @return {boolean | undefined}
		 */
		getBooleanProperty: function(xml, p, defaultValue) {
			// <XML P="...">
			const v = this.getProperty(xml, p, defaultValue ? BOOLEAN_TRUE : undefined);
			return (v == undefined) ? v : (v == BOOLEAN_TRUE || v == BOOLEAN_ONE);
		},

		/**
		 * Wait certain number of time before the promise resolve
		 * @param {number} [milliseconds] - wait time before calling resolve function
		 * @return {Promise}
		 */
		wait: function(milliseconds) {
			var waitTime = milliseconds || 0;
			return new Promise(function(resolve, reject) {
				setTimeout(function() {
					resolve()
				}, waitTime);
			});

		}
	};
});
